import {
  type CatalogueItemResource,
  OutletCatalogueController_read_v1,
  type OutletResource,
  OutletsController_read_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle, IconArrowLeft } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { isForbidden, isNotFound } from '@/api/failure';
import { useSession } from '@/auth/session';
import { formatPaise } from '@/lib/money';

import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import {
  isRetired as isOutletRetired,
  labelForKind as labelForOutletKind,
  labelForStock,
} from '../outlets/outlet-kinds';
import { catalogueAvailabilityPath, paths } from '../paths';
import {
  CATALOGUE_KIND_ITEMS,
  CATALOGUE_KINDS,
  type CatalogueKind,
  labelForKind,
} from './catalogue-kinds';

/**
 * What one outlet sells: `GET /v1/outlets/{id}/catalogue`.
 *
 * **The back-office twin of what a till holds**, and a screen rather than a debug
 * view: "what can the cafeteria actually sell" is a question a manager asks
 * before a shift, and the alternative to answering it here is somebody opening
 * every item in the catalogue and reading its availability one at a time.
 *
 * It is the other direction of `ItemAvailability`, which is where the set is
 * *changed*. This one only reads — there is no control on a row and no way to
 * withdraw an item from here, because withdrawing is an operation on the *item's*
 * set and doing it from this end would be an add-and-remove interface onto a
 * replace-the-whole-set route. An owner follows an item's name to the screen that
 * owns the write.
 *
 * ## Paged, and that is the choice rather than the default
 *
 * `docs/ui-conventions.md`, "Reading a list", asks that the strategy be picked.
 * This set grows with **use** — every product, ticket type and activity the
 * garden ever sells here, for as long as it sells things — so it pages, exactly
 * as the catalogue list does. The API asserts the read costs a fixed number of
 * statements whatever the size of the catalogue (FR-209, SC-205), so there is
 * nothing to fear in a large park and nothing to gain from asking for it all at
 * once.
 *
 * The outlet in the header beside it is one read of one row, and the item
 * availability screen this links to reads *its* set whole — bounded by the park.
 * Getting those two the same way round is the mistake the conventions document
 * exists to prevent.
 *
 * ## What is absent, and why that is not a bug
 *
 * Retired items and withdrawn availability are both absent, by the API's own
 * decision. So is `costPaise`, for every caller including an owner — this is a
 * `CatalogueItemResource`, which has no such field. The screen says what it is
 * showing rather than leaving a manager to work out whether the list is short
 * because nothing sells here or because something was filtered.
 */

/** Every state the rows can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly items: readonly CatalogueItemResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The outlet in the header, in the same three states. */
type OutletState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly outlet: OutletResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The value the kind filter carries when it is not filtering. */
const ANY = 'ALL';

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 4;

export function OutletCatalogue() {
  const client = useApiClient();
  const { session } = useSession();
  const { id: outletId } = useParams<{ id: string }>();

  const [kind, setKind] = useState<CatalogueKind | typeof ANY>(ANY);
  const [offset, setOffset] = useState(0);

  const [outletState, setOutletState] = useState<OutletState>({ kind: 'loading' });
  const [state, setState] = useState<ListState>({ kind: 'loading' });

  // The window the last successful read answered with, kept **outside**
  // `ListState` on purpose: it survives the next load, so the pager and the count
  // stay where they are while a page is in flight instead of vanishing and coming
  // back under the pointer. `null` until the first answer.
  const [page, setPage] = useState<PageState | null>(null);

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  // Changing where an item sells is `@RequireRole('OWNER')` on the API
  // ("only the owner sets availability", CL-206), so a manager gets the item's
  // name as plain text rather than a link onto a screen that can only refuse.
  // Hidden rather than shown and 403'd, per docs/ui-conventions.md; the role came
  // out of `sessionStorage` and is editable, so this is courtesy and the route
  // guard and the server are the enforcement.
  const mayChangeAvailability = session?.user.role === 'OWNER';

  useEffect(() => {
    if (outletId === undefined) return;

    const controller = new AbortController();
    let live = true;

    setOutletState({ kind: 'loading' });

    client
      .request(OutletsController_read_v1, {
        path: { id: outletId },
        signal: controller.signal,
      })
      .then((outlet) => {
        if (live) setOutletState({ kind: 'loaded', outlet });
      })
      .catch((error: unknown) => {
        if (live) setOutletState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, reloads]);

  useEffect(() => {
    if (outletId === undefined) return;

    // Two guards, not one. `controller` stops the request itself the moment the
    // filter or the page moves, and `live` stops an answer that was already in
    // flight from landing after this effect was torn down — without it, pressing
    // Next twice quickly can leave the second page's rows underneath the first's.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(OutletCatalogueController_read_v1, {
        path: { id: outletId },
        query: {
          offset,
          limit: ROWS_PER_PAGE,

          // Sent only when it is filtering. The API answers with every kind when
          // it is omitted, and the query string is what appears in a log when
          // somebody is working out what a screen asked for.
          ...(kind === ANY ? {} : { kind }),
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({ kind: 'loaded', items: answer.items });

          // The window the *server* answered with, not the one that was asked
          // for. They are the same today, and a pager that assumed so would be
          // silently wrong the day the API clamps a limit.
          setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
        }
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, kind, offset, reloads]);

  // A route matched on `:id` always has one; this is the shape of somebody
  // reaching the URL some other way.
  if (outletId === undefined) return <Navigate to={paths.catalogue} replace />;

  const outlet = outletState.kind === 'loaded' ? outletState.outlet : null;

  // Either read refusing is the same refusal, and either one answering "no such
  // outlet" is the same answer — an outlet outside the caller's reach is
  // indistinguishable from an unknown one by the API's design (FR-128) — so both
  // are answered once for the screen rather than twice in two different words.
  const refused =
    (outletState.kind === 'failed' && isForbidden(outletState.error)) ||
    (state.kind === 'failed' && isForbidden(state.error));

  const missing =
    (outletState.kind === 'failed' && isNotFound(outletState.error)) ||
    (state.kind === 'failed' && isNotFound(state.error));

  // A retired outlet still *reads* — its business days and ledger entries name
  // it, so `GET /v1/outlets/{id}` answers for one (FR-112) — but its catalogue
  // does not: that route refuses a retired outlet exactly as it refuses an
  // unknown one. Answered before `missing` so the screen says the true thing
  // rather than "there is no such outlet" about an outlet it is naming in the
  // rail.
  const retiredOutlet = outlet !== null && isOutletRetired(outlet);

  return (
    <Screen
      title="What this outlet sells"
      description="Everything sellable here, with the price in force. It is the same list a till at this outlet holds. Retired items and withdrawn availability are both absent, so what is here is what can be rung up."
      aside={
        <>
          {outlet !== null && (
            <ScreenFact label="Outlet">
              <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
              {isOutletRetired(outlet) && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an outlet is an
                      ordinary end to an ordinary life. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
              <br />
              <span className="text-muted-foreground">
                {labelForOutletKind(outlet.kind)},{' '}
                {labelForStock(outlet.carriesStock).toLowerCase()}
              </span>
            </ScreenFact>
          )}

          {page !== null && state.kind !== 'failed' && (
            <ScreenFact label="On sale here">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'item' : 'items'}
              {kind !== ANY && ` of that kind`}
            </ScreenFact>
          )}

          <div className="space-y-2">
            <Label htmlFor="kind-filter" className="text-sm text-muted-foreground">
              Kind
            </Label>
            <Select
              value={kind}
              items={[{ label: 'All kinds', value: ANY }, ...CATALOGUE_KIND_ITEMS]}
              // `null` is Base UI's "cleared", which this picker has no
              // affordance for — no clear button and no empty option. Read as
              // "not filtering" rather than cast away, so a cleared select can
              // never become a `kind=null` in the query string.
              onValueChange={(next) => {
                setKind(next ?? ANY);

                // Any filter moving puts the list back at the first page. Left
                // alone, a filter applied at offset 200 lands past the end of a
                // shorter result and reads as "nothing sells here".
                setOffset(0);
              }}
            >
              <SelectTrigger id="kind-filter" aria-label="Kind" className="h-10 w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>All kinds</SelectItem>
                  {CATALOGUE_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {labelForKind(option)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <Link
            to={paths.catalogue}
            className="inline-flex items-center gap-1.5 rounded-3xl text-sm text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <IconArrowLeft className="size-4" aria-hidden />
            Back to the catalogue
          </Link>
        </>
      }
    >
      {refused ? (
        <NotYours />
      ) : retiredOutlet ? (
        <OutletRetired />
      ) : missing ? (
        <OutletMissing />
      ) : (
        <>
          <ScreenTable label="What this outlet sells">
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-36">Kind</TableHead>
                {/* Amounts right-align, per docs/ui-conventions.md — comparing
                    down a column is the task. */}
                <TableHead className="w-40 text-right">Selling price</TableHead>
              </TableRow>
            </TableHeader>

            {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

            {state.kind === 'failed' && (
              <TableNotice columns={COLUMNS}>
                <LoadFailed error={state.error} onRetry={reload} />
              </TableNotice>
            )}

            {state.kind === 'loaded' &&
              (state.items.length === 0 ? (
                <TableNotice columns={COLUMNS}>
                  <EmptyState
                    title={kind === ANY ? 'Nothing sells here yet' : 'Nothing of that kind here'}
                    description={
                      kind === ANY
                        ? 'No item in the catalogue has been made available at this outlet. An owner sets that on the item itself, under where it sells.'
                        : 'This outlet sells nothing of that kind. Widen the filter to see everything it does sell.'
                    }
                  />
                </TableNotice>
              ) : (
                <TableBody>
                  {state.items.map((item) => (
                    <SoldItemRow
                      key={item.id}
                      item={item}
                      linkAvailability={mayChangeAvailability}
                    />
                  ))}
                </TableBody>
              ))}
          </ScreenTable>

          {/* Not while the load failed: the notice above already says the rows
              could not be read, and a pager under it offers to move through rows
              that are not there. `Pager` draws nothing when everything fits on
              one page. */}
          {page !== null && state.kind !== 'failed' && (
            <Pager state={page} label="items sold here" onOffsetChange={setOffset} />
          )}
        </>
      )}
    </Screen>
  );
}

/**
 * One item sold here. No control of its own beyond the way to the screen that
 * owns the write — see the note at the top of this file for why there is no
 * "withdraw" button on this end.
 */
function SoldItemRow({
  item,
  linkAvailability,
}: {
  item: CatalogueItemResource;

  /** Whether the name opens this item's availability. Owner only. */
  linkAvailability: boolean;
}) {
  return (
    <TableRow>
      {/* A code is read aloud and typed back in, so it gets the mono face and
          tabular figures — and this one is on receipts and tickets. */}
      <TableCell className="font-mono text-foreground numeric">{item.code}</TableCell>

      <TableCell className="whitespace-normal">
        {linkAvailability ? (
          // A control with no chrome, as the name is in the catalogue list. This
          // screen has no accent of its own — it changes nothing — so the link
          // is discoverable on hover and focus without competing with anything.
          <Link
            to={catalogueAvailabilityPath(item.id)}
            // Named per row, because "Where it sells" repeated down a list of
            // links is no help to somebody navigating by them.
            aria-label={`Where ${item.name} sells`}
            className="rounded-sm text-foreground underline-offset-4 outline-none hover:underline focus-visible:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            {item.name}
          </Link>
        ) : (
          <span className="text-foreground">{item.name}</span>
        )}
      </TableCell>

      <TableCell>{labelForKind(item.kind)}</TableCell>

      <TableCell className="text-right font-mono text-foreground numeric">
        {formatPaise(item.sellingPaise)}
      </TableCell>
    </TableRow>
  );
}

/**
 * The outlet is not one this caller may read.
 *
 * **Not `LoadFailed`.** It was read, and the answer was no. No retry either:
 * `AUTH_FORBIDDEN` is non-retryable in the API's own registry.
 */
function NotYours() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>This outlet is not yours to read</AlertTitle>
      <AlertDescription>
        What an outlet sells is readable by the people who work there and by the back office. Go
        back to the catalogue and pick an outlet from the &ldquo;Sold at&rdquo; filter.
      </AlertDescription>
    </Alert>
  );
}

/**
 * The outlet is retired, so it sells nothing and this route refuses it.
 *
 * Worded as a fact about the outlet rather than as a failure: the availability
 * rows survive an outlet's retirement, so this is not "the list is empty" and it
 * is not "something went wrong" — it is a place that has stopped trading.
 */
function OutletRetired() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>This outlet has been retired</AlertTitle>
      <AlertDescription>
        A retired outlet sells nothing, so there is no catalogue to show for it. Its sales, tickets
        and audit entries stay reachable from the outlets screen, and the items that were available
        here are still in the catalogue.
      </AlertDescription>
    </Alert>
  );
}

/**
 * There is no such outlet — or it is outside this caller's reach.
 *
 * The three are one answer by the API's design (FR-128), so this says the one
 * thing that is true of all of them rather than guessing which it was.
 */
function OutletMissing() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>There is no such outlet</AlertTitle>
      <AlertDescription>
        Nothing in the park has this address. It may have been opened from a link that is out of
        date, or the outlet may have been retired since. Go back to the catalogue and pick an outlet
        from the &ldquo;Sold at&rdquo; filter.
      </AlertDescription>
    </Alert>
  );
}
